import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { ScrollText, Search, RefreshCw, Download } from "lucide-react";

const ACTION_LABELS = {
  comp: { text: "Comp", tone: "bg-blue-50 text-blue-700" },
  bulk_comp: { text: "Bulk comp", tone: "bg-purple-50 text-purple-700" },
  create: { text: "Create", tone: "bg-emerald-50 text-emerald-700" },
};

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return String(v).slice(0, 16); }
};
const fmtDateOnly = (v) => (v ? String(v).slice(0, 10) : "—");

const SponsorLogPanel = () => {
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (action) params.set("action", action);
      params.set("limit", "100");
      const r = await api.get(`/admin/sponsor/log?${params.toString()}`);
      setEntries(r.data.entries || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || `Failed to load (HTTP ${e?.response?.status || "network"}).`);
    } finally { setBusy(false); }
  }, [q, action]);

  useEffect(() => { load(); }, [load]);

  const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const downloadCsv = () => {
    if (entries.length === 0) return;
    const cols = ["created_at", "action", "event_id", "event_name", "event_slug",
      "host_name", "host_email", "prior_tier", "new_tier", "prior_expires_at",
      "new_expires_at", "admin_email", "reason"];
    const header = cols.join(",");
    const rows = entries.map((e) => cols.map((c) => csvEscape(e[c])).join(","));
    const blob = new Blob([`\ufeff${header}\n${rows.join("\n")}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `sponsor-log-${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  return (
    <div data-testid="sponsor-log-panel" className="bg-white rounded-3xl soft-shadow p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-coral-300" />
          <h3 className="font-serif text-navy-700 text-xl">Sponsor log</h3>
          <span className="text-xs text-navy-700/50">{total} entries</span>
        </div>
        <div className="flex items-center gap-2">
        <button onClick={load} disabled={busy} data-testid="sponsor-log-refresh"
          className="inline-flex items-center gap-1.5 border border-navy-700/20 text-navy-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-cream-100 disabled:opacity-60">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
        <button onClick={downloadCsv} disabled={entries.length === 0} data-testid="sponsor-log-csv"
          className="inline-flex items-center gap-1.5 bg-navy-700 text-white px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-navy-800 disabled:opacity-60">
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
        </div>
      </div>

      <p className="text-sm text-navy-700/60 mt-2">
        Every comp / create / bulk-reactivate action, with a searchable audit trail.
      </p>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-4 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy-700/40" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="sponsor-log-search"
            placeholder="Search by event name, host email, admin, reason…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
          />
        </div>
        <select value={action} onChange={(e) => setAction(e.target.value)}
          data-testid="sponsor-log-action-filter"
          className="px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm bg-white">
          <option value="">All actions</option>
          <option value="comp">Comp (single)</option>
          <option value="bulk_comp">Bulk comp</option>
          <option value="create">Create sponsored</option>
        </select>
        <button type="submit" disabled={busy} data-testid="sponsor-log-submit"
          className="bg-coral-300 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-coral-400 disabled:opacity-60">
          Search
        </button>
      </form>

      {err && <div className="mt-4 bg-red-50 text-red-700 text-sm rounded-md px-3 py-2" data-testid="sponsor-log-err">{err}</div>}

      <div className="mt-4 overflow-x-auto">
        {entries.length === 0 && !busy && !err && (
          <p className="text-sm text-navy-700/50 py-6 text-center">No log entries match.</p>
        )}
        {entries.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-navy-700/50 border-b border-cream-200">
                <th className="py-2 pr-3">When</th>
                <th className="pr-3">Action</th>
                <th className="pr-3">Event</th>
                <th className="pr-3">Host</th>
                <th className="pr-3">Change</th>
                <th className="pr-3">Admin</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-200">
              {entries.map((e, i) => {
                const label = ACTION_LABELS[e.action] || { text: e.action, tone: "bg-cream-100 text-navy-700/70" };
                return (
                  <tr key={i} data-testid={`sponsor-log-row-${i}`}>
                    <td className="py-3 pr-3 text-navy-700/70 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                    <td className="pr-3">
                      <span className={`text-[10px] uppercase tracking-wider ${label.tone} rounded px-2 py-1 whitespace-nowrap`}>{label.text}</span>
                    </td>
                    <td className="pr-3 min-w-[160px]">
                      <div className="font-medium text-navy-700 truncate max-w-[220px]">{e.event_name || "—"}</div>
                      <div className="text-[10px] text-navy-700/40 font-mono truncate max-w-[220px]">{e.event_id}</div>
                    </td>
                    <td className="pr-3 min-w-[160px]">
                      <div className="text-navy-700 truncate max-w-[200px]">{e.host_name || "—"}</div>
                      <div className="text-[11px] text-navy-700/50 truncate max-w-[200px]">{e.host_email || ""}</div>
                    </td>
                    <td className="pr-3 whitespace-nowrap text-navy-700/70">
                      {e.prior_tier ? <>{e.prior_tier} → </> : null}
                      <b className="text-navy-700">{e.new_tier}</b>
                      <div className="text-[11px] text-navy-700/50">until {fmtDateOnly(e.new_expires_at)}</div>
                    </td>
                    <td className="pr-3 text-navy-700/70 truncate max-w-[160px]">{e.admin_email || "—"}</td>
                    <td className="text-navy-700/70 max-w-[220px] truncate" title={e.reason || ""}>{e.reason || <span className="text-navy-700/30">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SponsorLogPanel;
